import {
  InputValue,
  InputValue as InputValueProto,
  type Question as QuestionProto,
  SelectInputValue,
  BoolInputValue as BoolInputValueProto,
  TextInputValue as TextInputValueProto,
  NumericInputValue as NumericInputValueProto,
  DateInputValue as DateInputValueProto,
} from '@taxfyle/api-internal/internal/question_input_value_pb'
import type { Map as MapProto } from 'google-protobuf'
import type {
  AnswersMap,
  BoolAnswer,
  DateTimeAnswer,
  MultiSelectAnswer,
  NumericAnswer,
  Question,
  QuestionAnswer,
  SingleSelectAnswer,
  TextAnswer,
} from '@/features/jobs/questions/questions'
import SelectionCase = SelectInputValue.SelectionCase
import TypeCase = InputValue.TypeCase
import { maybeParseDate, unixEpoch } from '@/utils/date-util'
import {
  decimalToNumber,
  numberToDecimal,
  timestampToISO,
  ISOToTimestamp,
} from '@/utils/grpc-util'

/**
 * Maps a question proto to the local type.
 *
 * @param proto
 */
export function mapQuestionProtoToQuestion(
  proto: QuestionProto,
): Question | null {
  const id = proto.getId()
  const title = proto.getQuestion()
  const description = proto.getDescription()?.getValue()
  const inputValue = proto.getAnswer()
  const props = {
    id,
    title,
    description,
  }

  switch (proto.getType()) {
    case 'Bool': {
      return {
        ...props,
        type: 'Bool',
        answer: inputValue ? mapInputValueProtoToBoolAnswer(inputValue) : null,
      }
    }
    case 'Select': {
      return {
        ...props,
        type: 'Select',
        multi: proto.getMulti()?.getValue() ?? false,
        options: proto.getOptionsList().map((o) => ({
          id: o.getId(),
          text: o.getText(),
        })),
        answer: inputValue
          ? mapInputValueProtoToSelectAnswer(inputValue)
          : null,
      }
    }
    case 'Text': {
      return {
        ...props,
        type: 'Text',
        answer: inputValue ? mapInputValueProtoToTextAnswer(inputValue) : null,
      }
    }
    case 'Numeric': {
      const formatProto = proto.getFormat()
      return {
        ...props,
        type: 'Numeric',
        format: formatProto
          ? formatProto.getType() === 'Integer'
            ? { type: 'Integer' }
            : {
                type: 'Decimal',
                places: formatProto.getPlaces(),
              }
          : { type: 'Integer' },
        answer: inputValue
          ? mapInputValueProtoToNumericAnswer(inputValue)
          : null,
      }
    }
    case 'DateTime': {
      return {
        ...props,
        type: 'DateTime',
        collectTime: proto.getCollectTime(),
        disallowPast: proto.getDisallowPast(),
        disallowFuture: proto.getDisallowFuture(),
        answer: inputValue
          ? mapInputValueProtoToDateTimeAnswer(inputValue)
          : null,
      }
    }
    case 'Breaker': {
      return {
        ...props,
        type: 'Breaker',
        answer: null,
      }
    }
    case 'Documents': {
      // Documents questions are no longer supported
      return null
    }
    default: {
      return {
        ...props,
        type: 'Unknown',
        answer: null,
      }
    }
  }
}

/**
 * Maps an input value proto to a {@link QuestionAnswer}
 * @param proto
 */
export function mapInputValueProtoToQuestionAnswer(
  proto: InputValueProto,
): QuestionAnswer {
  switch (proto.getTypeCase()) {
    case TypeCase.BOOL_VALUE:
      return mapInputValueProtoToBoolAnswer(proto)
    case TypeCase.SELECT_VALUE:
      return mapInputValueProtoToSelectAnswer(proto)
    case TypeCase.TEXT_VALUE:
      return mapInputValueProtoToTextAnswer(proto)
    case TypeCase.NUMERIC_VALUE:
      return mapInputValueProtoToNumericAnswer(proto)
    case TypeCase.DATE_VALUE:
      return mapInputValueProtoToDateTimeAnswer(proto)
    case TypeCase.DOCUMENTS_VALUE: // Documents questions are deprecated
    default:
      console.warn(new Error(`Unknown question type: ${proto.getTypeCase()}`))
      return {
        type: 'Unknown',
      }
  }
}

/**
 * Maps an input value proto to a {@link BoolAnswer}
 *
 * @param proto
 */
export function mapInputValueProtoToBoolAnswer(
  proto: InputValueProto,
): BoolAnswer {
  return {
    type: 'Bool',
    value: proto.getBoolValue()?.getValue() ?? false,
  }
}

/**
 * Maps an input value proto to a {@link BoolAnswer}
 *
 * @param proto
 */
export function mapInputValueProtoToSelectAnswer(
  proto: InputValueProto,
): SingleSelectAnswer | MultiSelectAnswer {
  const selection = proto.getSelectValue()
  switch (selection?.getSelectionCase()) {
    case SelectionCase.SINGLE: {
      return {
        type: 'SingleSelectAnswer',
        value: selection.getSingle()?.getSelectedId() ?? '(unknown value)',
      }
    }
    case SelectionCase.MULTI: {
      return {
        type: 'MultiSelectAnswer',
        value: selection.getMulti()?.getSelectedIdsList() ?? [],
      }
    }
  }

  return {
    type: 'MultiSelectAnswer',
    value: [],
  }
}

/**
 * Maps an input value proto to a {@link TextAnswer}
 *
 * @param proto
 */
export function mapInputValueProtoToTextAnswer(
  proto: InputValueProto,
): TextAnswer {
  return {
    type: 'Text',
    value: proto.getTextValue()?.getValue() ?? '',
  }
}

/**
 * Maps an input value proto to a {@link NumericAnswer}
 *
 * @param proto
 */
export function mapInputValueProtoToNumericAnswer(
  proto: InputValueProto,
): NumericAnswer {
  const value = proto.getNumericValue()?.getValue()
  return {
    type: 'Numeric',
    value: value ? decimalToNumber(value) : 0,
  }
}

/**
 * Maps an input value proto to a {@link DateTimeAnswer}
 *
 * @param proto
 */
export function mapInputValueProtoToDateTimeAnswer(
  proto: InputValueProto,
): DateTimeAnswer {
  return {
    type: 'DateTime',
    value:
      maybeParseDate(timestampToISO(proto.getDateValue()?.getValue())) ??
      unixEpoch(),
  }
}

/**
 * Populates (mutates!) the given map proto with the answer map by mapping
 * the answers to the proper proto.
 *
 * @param source
 * @param destProto
 */
export function populateAnswerMapProto(
  source: AnswersMap,
  destProto: MapProto<string, InputValueProto>,
) {
  for (const [id, answer] of source.entries()) {
    destProto.set(id, mapQuestionAnswerToInputValueProto(answer))
  }
}

/**
 * Maps a {@link QuestionAnswer} to an input value proto.
 *
 * @param answer
 */
export function mapQuestionAnswerToInputValueProto(
  answer: QuestionAnswer,
): InputValueProto {
  const proto = new InputValueProto()
  switch (answer.type) {
    case 'Bool':
      proto.setBoolValue(new BoolInputValueProto().setValue(answer.value))
      break
    case 'SingleSelectAnswer':
      proto.setSelectValue(
        new SelectInputValue().setSingle(
          new SelectInputValue.Single().setSelectedId(answer.value),
        ),
      )
      break
    case 'MultiSelectAnswer':
      proto.setSelectValue(
        new SelectInputValue().setMulti(
          new SelectInputValue.Multi().setSelectedIdsList(answer.value),
        ),
      )
      break
    case 'Text':
      proto.setTextValue(new TextInputValueProto().setValue(answer.value))
      break
    case 'Numeric':
      proto.setNumericValue(
        new NumericInputValueProto().setValue(numberToDecimal(answer.value)),
      )
      break
    case 'DateTime':
      proto.setDateValue(
        new DateInputValueProto().setValue(
          ISOToTimestamp(answer.value.toISOString()),
        ),
      )
      break
    default:
      console.warn(new Error(`Unknown answer type: ${answer.type}`))
  }
  return proto
}
